/**
 * Identity Hero 区域组件
 * 详情页顶部展示区域
 * 
 * 设计特点：
 * - 深色背景 + 数据流动画
 * - 大头像展示
 * - 状态信息和快捷操作
 */

import { useState } from 'react'
import { cn } from '@/shared/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  ArrowLeft,
  Play,
  Pause,
  MoreHorizontal,
  Archive,
  ArchiveRestore,
  Trash2,
  RefreshCw,
  Loader2,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { DataFlowAnimationSimple } from './DataFlowAnimation'
import { IdentityStatusBadge, VoiceStatusBadge, LearningProgress } from './StatusBadge'
import type { Identity, LearningStatus } from '@/modules/identity/types'

interface IdentityHeroProps {
  identity: Identity
  learningStatus?: LearningStatus | null
  onBack?: () => void
  onStartLearning?: () => void
  onCancelLearning?: () => void
  onArchive?: () => void
  onUnarchive?: () => void
  onDelete?: () => void
  onRefresh?: () => void
  isLoading?: boolean
  className?: string
}

export function IdentityHero({
  identity,
  learningStatus,
  onBack,
  onStartLearning,
  onCancelLearning,
  onArchive,
  onUnarchive,
  onDelete,
  onRefresh,
  isLoading,
  className,
}: IdentityHeroProps) {
  const [isActionLoading, setIsActionLoading] = useState(false)
  
  const isLearning = identity.status === 'learning'
  const isArchived = identity.status === 'archived'
  const canStartLearning = identity.status === 'draft' || identity.status === 'failed'
  
  // 头像首字母
  const avatarFallback = identity.name.slice(0, 2).toUpperCase()

  const handleAction = async (action: () => void | Promise<void>) => {
    setIsActionLoading(true)
    try {
      await action()
    } finally {
      setIsActionLoading(false)
    }
  }

  return (
    <div className={cn('relative', className)}>
      {/* 背景动画 */}
      <DataFlowAnimationSimple className="absolute inset-0" />
      
      {/* 内容层 */}
      <div className="relative z-10 px-6 py-8">
        {/* 顶部导航 */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回列表
          </Button>
          
          <div className="flex items-center gap-2">
            {/* 刷新按钮 */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRefresh?.()}
              disabled={isLoading}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
            
            {/* 更多操作 */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => isArchived ? onUnarchive?.() : onArchive?.()}>
                  {isArchived ? (
                    <>
                      <ArchiveRestore className="h-4 w-4 mr-2" />
                      恢复
                    </>
                  ) : (
                    <>
                      <Archive className="h-4 w-4 mr-2" />
                      归档
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  删除
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        {/* 主内容区 */}
        <div className="flex items-start gap-6">
          {/* 大头像 */}
          <Avatar className="h-28 w-28 rounded-2xl ring-4 ring-white/20 shadow-2xl">
            <AvatarImage src={identity.avatar} alt={identity.name} className="object-cover" />
            <AvatarFallback className="rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-white text-3xl font-bold">
              {avatarFallback}
            </AvatarFallback>
          </Avatar>
          
          {/* 信息区 */}
          <div className="flex-1 min-w-0">
            {/* 名称 + 状态 */}
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-white truncate">
                {identity.name}
              </h1>
              <IdentityStatusBadge status={identity.status} />
            </div>
            
            {/* 描述 */}
            {identity.description && (
              <p className="text-white/60 text-sm mb-4 line-clamp-2">
                {identity.description}
              </p>
            )}
            
            {/* 统计信息 */}
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-white/50">语料</span>
                <Badge variant="secondary" className="bg-white/10 text-white border-0">
                  {identity.corpus_count ?? 0} 条
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/50">声音</span>
                <VoiceStatusBadge status={identity.voice_status || 'none'} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/50">版本</span>
                <span className="text-white/80">v{identity.version ?? 1}</span>
              </div>
            </div>
            
            {/* 学习进度（仅学习中显示） */}
            {isLearning && learningStatus && (
              <div className="mt-4 max-w-md">
                <LearningProgress
                  progress={learningStatus.progress}
                  stage={learningStatus.stage_description}
                  className="[&_span]:text-white/60 [&_span]:text-white"
                />
                {learningStatus.estimated_remaining_time && (
                  <p className="text-xs text-white/40 mt-1">
                    预计剩余：{learningStatus.estimated_remaining_time}
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* 操作按钮区 */}
          <div className="flex flex-col gap-2">
            {canStartLearning && (
              <Button
                onClick={() => handleAction(() => onStartLearning?.())}
                disabled={isActionLoading || (identity.corpus_count ?? 0) === 0}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {isActionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                开始学习
              </Button>
            )}
            {isLearning && (
              <Button
                variant="secondary"
                onClick={() => handleAction(() => onCancelLearning?.())}
                disabled={isActionLoading}
              >
                {isActionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Pause className="h-4 w-4 mr-2" />
                )}
                取消学习
              </Button>
            )}
            {(identity.corpus_count ?? 0) === 0 && canStartLearning && (
              <p className="text-xs text-white/40 text-center">
                请先上传语料
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
